import React, { useState, useEffect, useCallback, useRef } from 'react';
import useUnifiedAudioRecorder from '../../hooks/useUnifiedAudioRecorder';
import { fetchBackendApiWithBearer } from '../../utils/apiUtils';
import { getAuthData } from '../../utils/sessionStorage';
import { listCaseAudioSessions, getAudioSessionStatus } from '../../services/api';
import { logAndInterpretError } from '../../utils/errorInterpreter';
import CaseInfoDisplay from '../shared/CaseInfoDisplay';
import AllRecordings from '../shared/AllRecordings';
import AudioVisualizer from '../shared/AudioVisualizer';
import { checkNativePlatform } from '../../utils/nativeApiUtils';
import EnhancedTranscript from '../shared/EnhancedTranscript';

/**
 * InPersonAudioRecorder - Component for in-person audio recording sessions
 * Uses the unified WebSocket architecture for MP3-first recording with optional real-time transcription
 * Includes case selection/creation functionality
 */
const InPersonAudioRecorder = ({
    providerInfo,
    healthcareInfo,
    selectedCase = null,
    onClose,
    onTranscriptionUpdate,
    onVisitNoteReady,
    enableRealtime = false,
    className = ''
}) => {
    const {
        connectionState,
        isRecording,
        sessionId,
        error,
        transcription,
        visitNote,
        audioSegments,
        processingStatus,
        audioLevel,
        microphoneStatus,
        startRecording,
        stopRecording,
        endSession,
        pauseSegment,
        resumeSegment,
        generateNote,
        clearError
    } = useUnifiedAudioRecorder();
    
    // Recording state
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [micPermission, setMicPermission] = useState('prompt'); // 'granted', 'denied', 'prompt'
    const [showAllRecordings, setShowAllRecordings] = useState(false);
    
    // Case selection state
    const [activeTab, setActiveTab] = useState('existing'); // 'existing' or 'new'
    const [localSelectedCase, setLocalSelectedCase] = useState(selectedCase);
    const [patientSearchQuery, setPatientSearchQuery] = useState('');
    
    // Initialize selected case from props
    useEffect(() => {
        if (selectedCase) {
            setLocalSelectedCase(selectedCase);
        }
    }, [selectedCase]);
    
    const [patientSearchResults, setPatientSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    
    // New patient form state
    const [newPatientData, setNewPatientData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        dob: '',
        ehr_medical_record_number: '',
        phone: '',
        email: '',
        gender: ''
    });
    
    const [isCreatingCase, setIsCreatingCase] = useState(false);
    
    // Existing sessions state
    const [existingSessions, setExistingSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [activeSessionDetails, setActiveSessionDetails] = useState(null);
    
    // Unified error handling
    const [currentError, setCurrentError] = useState({ message: '', type: '' });
    const errorTimeoutRef = useRef(null);
    
    // Search timeout ref
    const searchTimeoutRef = useRef(null);
    
    // Rotating message state
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const rotatingMessages = [
        "I don't type as fast as you, Doc 😀. Give me 1-2 minutes and I'll get the note ready for ya.",
        "Analyzing your conversation and organizing the medical information...",
        "Writing your SOAP note with proper medical terminology and structure...",
        "You don't have to stay on this page. Just come back to this case and you'll see the note.",
        "Almost done! Putting the finishing touches on your visit note..."
    ];
    
    // Unified error handler with auto-clear after 5 seconds
    const showError = useCallback((message, type = 'warning') => {
        setCurrentError({ message, type });
        
        // Clear any existing timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }
        
        // Set new timeout to clear error after 5 seconds
        errorTimeoutRef.current = setTimeout(() => {
            setCurrentError({ message: '', type: '' });
        }, 5000);
    }, []);
    
    const clearCurrentError = useCallback(() => {
        setCurrentError({ message: '', type: '' });
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }
    }, []);
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, []);
    
    // Rotate messages when processing
    useEffect(() => {
        let messageInterval;
        if (processingStatus === 'processing' || processingStatus === 'generating_note') {
            messageInterval = setInterval(() => {
                setCurrentMessageIndex((prevIndex) => 
                    (prevIndex + 1) % rotatingMessages.length
                );
            }, 5500); // Change message every 5.5 seconds
        } else {
            setCurrentMessageIndex(0); // Reset to first message when not processing
        }
        return () => {
            if (messageInterval) {
                clearInterval(messageInterval);
            }
        };
    }, [processingStatus, rotatingMessages.length]);

    // Load existing audio sessions when case is selected
    useEffect(() => {
        const loadExistingSessions = async () => {
            if (!localSelectedCase?.case_id && !localSelectedCase?.id) {
                setExistingSessions([]);
                setActiveSessionDetails(null);
                return;
            }

            const caseId = localSelectedCase.case_id || localSelectedCase.id;
            const authData = getAuthData();
            const apiToken = authData?.api_token;

            if (!apiToken) {
                return;
            }

            setLoadingSessions(true);
            try {
                // Fetch all audio sessions for this case
                const sessionsData = await listCaseAudioSessions(apiToken, caseId);
                setExistingSessions(sessionsData.sessions || []);

                // Get detailed status for active session OR most recent completed session
                const activeSession = sessionsData.sessions?.find(s => s.status === 'active');
                const mostRecentCompleted = sessionsData.sessions
                    ?.filter(s => s.status === 'completed')
                    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

                const sessionToFetch = activeSession || mostRecentCompleted;

                if (sessionToFetch) {
                    try {
                        const sessionDetails = await getAudioSessionStatus(apiToken, sessionToFetch.session_id);
                        setActiveSessionDetails(sessionDetails);
                    } catch (error) {
                        setError('Unable to show session details:', error)
                    }
                } else {
                    setActiveSessionDetails(null);
                }
            } catch (error) {
                const userMessage = logAndInterpretError(error, 'Loading existing audio sessions');
                showError(userMessage);
            } finally {
                setLoadingSessions(false);
            }
        };

        loadExistingSessions();
    }, [localSelectedCase, showError]);
    
    // Check microphone permissions on mount
    useEffect(() => {
        checkMicrophonePermission();
    }, []);
    
    const checkMicrophonePermission = async () => {
        try {
            const result = await navigator.permissions.query({ name: 'microphone' });
            setMicPermission(result.state);
            
            result.addEventListener('change', () => {
                setMicPermission(result.state);
            });
        } catch (error) {
            // Fallback for browsers that don't support permissions API
            // Will request permission on start
        }
    };
    
    const requestMicrophonePermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Stop the stream immediately as we just needed permission
            stream.getTracks().forEach(track => track.stop());
            setMicPermission('granted');
            return true;
        } catch (error) {
            setMicPermission('denied');
            setError('Microphone access is required to record audio. Please grant microphone permission in your browser settings and try again.');
            return false;
        }
    };
    
    // Timer for recording duration
    useEffect(() => {
        let interval;
        if (isRecording && !isPaused) {
            interval = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isRecording, isPaused]);
    
    // Handle transcription updates
    useEffect(() => {
        if (transcription && onTranscriptionUpdate) {
            onTranscriptionUpdate(transcription);
        }
    }, [transcription, onTranscriptionUpdate]);
    
    // Handle visit note ready
    useEffect(() => {
        if (visitNote && onVisitNoteReady) {
            // Pass the content string, not the whole object
            const noteContent = typeof visitNote === 'string' ? visitNote : visitNote.content || '';
            onVisitNoteReady(noteContent);
        }
    }, [visitNote, onVisitNoteReady]);
    // Patient search function using correct endpoint with Bearer token
    const searchPatients = useCallback(async (query) => {
        if (!query.trim()) {
            setPatientSearchResults([]);
            return;
        }
        
        setIsSearching(true);
        clearCurrentError();
        
        try {
            // Get the API token from centralized storage
            const authData = getAuthData();
            const apiToken = authData?.api_token;
            if (!apiToken) {
                showError('Authentication token not available');
                return;
            }
            
            // Use the consolidated Bearer token function
            const searchEndpoint = `/healthcare/${providerInfo?.provider_id || providerInfo?.id}/patients/search/`;
            const queryParams = new URLSearchParams({ search: query.trim() });
            
            const response = await fetchBackendApiWithBearer(
                `${searchEndpoint}?${queryParams}`,
                apiToken,
                { method: 'GET' }
            );
            
            if (response.ok) {
                const data = await response.json();
                setPatientSearchResults(data.patients || []);
            } else {
                const errorData = await response.json().catch(() => ({}));
                showError(errorData.message || 'Failed to search patients');
            }
        } catch (error) {
            const userMessage = logAndInterpretError(error, 'Patient search');
            showError(userMessage);
        } finally {
            setIsSearching(false);
        }
    }, [providerInfo]);
    
    // Handle patient search input with debouncing
    const handlePatientSearch = useCallback((e) => {
        const query = e.target.value;
        setPatientSearchQuery(query);
        
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // Debounce search
        searchTimeoutRef.current = setTimeout(() => {
            searchPatients(query);
        }, 300);
    }, [searchPatients]);
    
    // Select existing patient and create case
    const handleSelectPatient = useCallback(async (patient) => {
        try {
            // Get the API token from centralized storage
            const authData = getAuthData();
            const apiToken = authData?.api_token;
            if (!apiToken) {
                showError('Authentication token not available');
                return;
            }
            // Use the provider initiate-visit endpoint with consolidated Bearer token function
            const caseData = {
                patient_info: {
                    patient_id: patient.patient_id || '',
                    patient_uuid: patient.id || '',
                    first_name: patient.first_name,
                    last_name: patient.last_name,
                    dob: patient.dob,
                    email: patient.email || '',
                    phone: patient.phone || '',
                    locale: patient.locale || 'en-US',
                    timezone: patient.timezone || 'America/New_York'
                },
                provider_info: providerInfo,
                healthcare_info: healthcareInfo,
                initial_forms_data: [],
                initial_insurance_data: {},
                include_zoom_session: false
            };
            
            const response = await fetchBackendApiWithBearer('/provider/initiate-visit/', apiToken, {
                method: 'POST',
                body: JSON.stringify(caseData)
            });
            
            if (response.ok) {
                const newCase = await response.json();
                setLocalSelectedCase({
                    ...newCase,
                    patient: patient
                });
                clearCurrentError();
            } else {
                const errorData = await response.json().catch(() => ({}));
                showError(errorData.message || 'Failed to create case for selected patient');
            }
        } catch (error) {
            const userMessage = logAndInterpretError(error, 'Case creation');
            showError(userMessage);
        }
    }, [providerInfo, healthcareInfo]);
    
    // Create new patient and case
    const handleCreateNewCase = useCallback(async () => {
        if (!newPatientData.first_name || !newPatientData.last_name || !newPatientData.dob) {
            showError('First name, last name, and date of birth are required');
            return;
        }
        
        setIsCreatingCase(true);
        clearCurrentError();
        
        try {
            // Get the API token from centralized storage
            const authData = getAuthData();
            const apiToken = authData?.api_token;
            if (!apiToken) {
                showError('Authentication token not available');
                return;
            }
            const caseData = {
                patient_info: {
                    ...newPatientData,
                    locale: 'en-US',
                    timezone: 'America/New_York'
                },
                provider_info: providerInfo,
                healthcare_info: healthcareInfo,
                initial_forms_data: [],
                initial_insurance_data: {},
                include_zoom_session: false
            };
            
            const response = await fetchBackendApiWithBearer('/provider/initiate-visit/', apiToken, {
                method: 'POST',
                body: JSON.stringify(caseData)
            });
            
            if (response.ok) {
                const newCase = await response.json();
                setLocalSelectedCase(newCase);
                clearCurrentError();
                
                // Reset form
                setNewPatientData({
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                    dob: '',
                    ehr_medical_record_number: '',
                    phone: '',
                    email: '',
                    gender: ''
                });
            } else {
                const errorData = await response.json().catch(() => ({}));
                showError(errorData.message || 'Failed to create new case');
            }
        } catch (error) {
            const userMessage = logAndInterpretError(error, 'New case creation');
            showError(userMessage);
        } finally {
            setIsCreatingCase(false);
        }
    }, [newPatientData, providerInfo, healthcareInfo]);
    
    
    // Start recording handler
    const handleStartRecording = useCallback(async () => {
        if (!localSelectedCase) {
            showError('Please select or create a case before recording');
            return;
        }
        
        // Check microphone permission first
        if (micPermission !== 'granted') {
            showError('Microphone permission is required. Please allow access when prompted.');
            const granted = await requestMicrophonePermission();
            if (!granted) {
                showError('Microphone permission denied. Please enable microphone access in your browser settings and try again.');
                return;
            }
            clearCurrentError(); // Clear error on success
        }
        
        try {
            clearCurrentError();
            await startRecording('in_person', localSelectedCase.case_id || localSelectedCase.id, {
                enableRealtime,
                providerInfo
            });
            setHasStarted(true);
            setRecordingDuration(0);
        } catch (error) {
            const userMessage = logAndInterpretError(error, 'Starting audio recording');
            showError(userMessage);
        }
    }, [startRecording, localSelectedCase, enableRealtime, providerInfo, micPermission, showError]);
    
    // Stop recording handler
    const handleStopRecording = useCallback(async () => {
        try {
            await stopRecording();
        } catch (error) {
            showError('Failed to stop recording: ',error);
        }
    }, [stopRecording]);
    
    // Pause recording handler
    const handlePauseRecording = useCallback(() => {
        pauseSegment();
        setIsPaused(true);
    }, [pauseSegment]);
    
    // Resume recording handler
    const handleResumeRecording = useCallback(() => {
        resumeSegment();
        setIsPaused(false);
    }, [resumeSegment]);
    
    // End session handler
    const handleEndSession = useCallback(async () => {
        try {
            await endSession();
            setHasStarted(false);
            setRecordingDuration(0);
            setIsPaused(false);
        } catch (error) {
            showError('Failed to end session: ', error);
        }
    }, [endSession]);
    
    // Generate note handler
    const handleGenerateNote = useCallback(() => {
        generateNote();
    }, [generateNote]);
    
    // Format duration helper
    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Get connection status display
    const getConnectionStatus = () => {
        switch (connectionState) {
            case 'connecting':
                return { text: 'Connecting...', color: 'text-warning', icon: '🔄' };
            case 'connected':
                return { text: 'Connected', color: 'text-success', icon: '✅' };
            case 'reconnecting':
                return { text: 'Reconnecting...', color: 'text-warning', icon: '🔄' };
            case 'error':
                return { text: 'Connection Error', color: 'text-danger', icon: '❌' };
            default:
                return { text: 'Disconnected', color: 'text-muted', icon: '⚫' };
        }
    };
    
    const connectionStatus = getConnectionStatus();
    
    return (
        <div className={` ${className}`} style={{position:'relative', height: '100%', display: 'flex', flexDirection: 'column'}}>
            <div className="card border-0" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-microphone me-2"></i>
                        In-Person Visit
                    </h5>
                    <div className="d-flex align-items-center">
                        <span className={`badge ${connectionStatus.color.replace('text-black', 'bg-')} me-2`}>
                            {connectionStatus.icon} {connectionStatus.text}
                        </span>
                        {sessionId && (
                            <small className="text-muted me-2">
                                Session: {sessionId.slice(0, 8)}...
                            </small>
                        )}
                        {onClose && (
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={onClose}
                                title="Close recorder"
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>
                
                <div className="card-body" style={{flex: 1, overflowY: 'auto'}}>
                    {/* Unified Error Display */}
                    {currentError.message && (
                        <div className={`alert alert-${currentError.type === 'error' ? 'danger' : 'warning'} alert-dismissible fade show`} role="alert">
                            {currentError.message}
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={clearCurrentError}
                                aria-label="Close"
                            ></button>
                        </div>
                    )}
                    
                    {/* Hook error display */}
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {error}
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={clearError}
                                aria-label="Close"
                            ></button>
                        </div>
                    )}
                    
                    {/* Case Selection */}
                    {!localSelectedCase && (
                        <div className="">
                            {/* <h6 className="alert-heading">Select or Create a Case</h6> */}
                            
                            
                            {/* Tab Navigation */}
                            <ul className="nav nav-tabs" role="tablist">
                                <li className="nav-item" role="presentation">
                                    <button 
                                        className={`nav-link ${activeTab === 'existing' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('existing')}
                                        type="button"
                                    >
                                        Select Existing Patient
                                    </button>
                                </li>
                                <li className="nav-item" role="presentation">
                                    <button 
                                        className={`nav-link ${activeTab === 'new' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('new')}
                                        type="button"
                                    >
                                        Create New Patient
                                    </button>
                                </li>
                            </ul>
                            
                            {/* Tab Content */}
                            <div className="tab-content">
                                {/* Existing Patient Tab */}
                                {activeTab === 'existing' && (
                                    <div className="tab-pane fade show active">
                                        <div className="">
                                            <div className="mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="patientSearch"
                                                    placeholder="Search by name, patient ID, email, or phone..."
                                                    value={patientSearchQuery}
                                                    onChange={handlePatientSearch}
                                                />
                                                {isSearching && (
                                                    <div className="mt-2">
                                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                        Searching...
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Search Results */}
                                            {patientSearchResults.length > 0 && (
                                                <div className="list-group mt-3">
                                                    {patientSearchResults.map((patient, index) => (
                                                        <div 
                                                            key={patient.id || index}
                                                            className="list-group-item list-group-item-action"
                                                            onClick={() => handleSelectPatient(patient)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <strong>
                                                                        {patient.first_name} {patient.last_name}
                                                                    </strong>
                                                                    <br />
                                                                    <small className="text-muted">
                                                                        DOB: {patient.dob} | 
                                                                        ID: {patient.patient_id || patient.id}
                                                                        {patient.email && ` | ${patient.email}`}
                                                                    </small>
                                                                </div>
                                                                <button className="btn btn-sm btn-primary">
                                                                    Select
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {/* New Patient Tab */}
                                {activeTab === 'new' && (
                                    <div className="tab-pane fade show active">
                                        <div className="mt-3">
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="firstName" className="form-label">
                                                        First Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="firstName"
                                                        value={newPatientData.first_name}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            first_name: e.target.value
                                                        }))}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="middleName" className="form-label">
                                                        Middle Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="middleName"
                                                        value={newPatientData.middle_name}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            middle_name: e.target.value
                                                        }))}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="lastName" className="form-label">
                                                        Last Name *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="lastName"
                                                        value={newPatientData.last_name}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            last_name: e.target.value
                                                        }))}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="dob" className="form-label">
                                                        Date of Birth *
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        id="dob"
                                                        value={newPatientData.dob}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            dob: e.target.value
                                                        }))}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="mrn" className="form-label">
                                                        Medical Record Number
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="mrn"
                                                        value={newPatientData.ehr_medical_record_number}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            ehr_medical_record_number: e.target.value
                                                        }))}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="gender" className="form-label">
                                                        Gender
                                                    </label>
                                                    <select
                                                        className="form-control"
                                                        id="gender"
                                                        value={newPatientData.gender}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            gender: e.target.value
                                                        }))}
                                                    >
                                                        <option value="">Select Gender</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                        <option value="other">Other</option>
                                                        <option value="prefer_not_to_say">Prefer not to say</option>
                                                    </select>
                                                </div>
                                            </div>
                                            
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="phone" className="form-label">
                                                        Phone Number
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        id="phone"
                                                        value={newPatientData.phone}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            phone: e.target.value
                                                        }))}
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="email" className="form-label">
                                                        Email Address
                                                    </label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="email"
                                                        value={newPatientData.email}
                                                        onChange={(e) => setNewPatientData(prev => ({
                                                            ...prev,
                                                            email: e.target.value
                                                        }))}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="text-end">
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={handleCreateNewCase}
                                                    disabled={isCreatingCase || !newPatientData.first_name || !newPatientData.last_name || !newPatientData.dob}
                                                >
                                                    {isCreatingCase ? (
                                                        <>
                                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                            Creating...
                                                        </>
                                                    ) : (
                                                        'Create Case'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {/* Selected Case Display */}
                    {/* {localSelectedCase && (
                        <div className="alert alert-success mb-3">
                            <h6 className="alert-heading mb-2">
                                Case Created
                            </h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <strong>Patient:</strong> {localSelectedCase.patient?.first_name} {localSelectedCase.patient?.last_name}
                                    <br />
                                    <small className="text-muted">
                                        DOB: {localSelectedCase.patient?.dob} | 
                                        ID: {localSelectedCase.patient?.patient_id || localSelectedCase.patient?.id}
                                    </small>
                                </div>
                                <div className="col-md-6 text-end">
                                    <span className="badge bg-success">Ready to Record</span>
                                    <br />
                                    <button 
                                        className="btn btn-sm btn-outline-secondary mt-1"
                                        onClick={() => setLocalSelectedCase(null)}
                                    >
                                        Change Case
                                    </button>
                                </div>
                            </div>
                        </div>
                    )} */}

                    {/* Case Information Display */}
                    {localSelectedCase && (
                        <CaseInfoDisplay
                            caseId={localSelectedCase.case_id || localSelectedCase.id}
                            compact={true}
                            showPatientInfo={true}
                            showProviderInfo={true}
                            showSessionInfo={true}
                            className="mx-3 mt-2"
                        />
                    )}
                    
                    {/* Recording Status - Dynamic Interface */}
                    <div className="recording-interface text-center mt-3 py-3">
                        {!localSelectedCase ? (
                            // No case selected state
                            <div className="no-case-state">
                                <div className="mic-icon-container mb-3">
                                    <i className="fas fa-microphone-slash fa-3x text-muted opacity-50"></i>
                                </div>
                                <h5 className="text-muted">Case is required to start recording</h5>
                                <p className="text-muted small mb-0">Please select or create a case above to begin</p>
                            </div>
                        ) : !hasStarted ? (
                            // Case selected, ready to record
                            <div className="ready-state">
                                <div className="mic-icon-container mb-3">
                                    <button 
                                        className="btn btn-link p-0 border-0"
                                        onClick={handleStartRecording}
                                        disabled={connectionState === 'connecting' || micPermission === 'denied'}
                                        style={{ background: 'none' }}
                                    >
                                        <i className={`fas fa-microphone fa-3x ${
                                            micPermission === 'denied' ? 'text-muted' : 'text-primary'
                                        }`}></i>
                                    </button>
                                </div>
                                {micPermission === 'denied' ? (
                                    <>
                                        <h5 className="text-primary">Mic Check!</h5>
                                        <p className="text-muted small mb-0">Let's get your mic turned on so I can listen to your conversations</p>
                                        <button
                                            className="btn btn-warning btn-sm mt-2"
                                            onClick={requestMicrophonePermission}
                                            disabled={connectionState === 'connecting'}
                                        >
                                            <i className="fas fa-microphone me-2"></i>
                                            Grant Mic Access
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <h5 className="text-primary">Awesome! Press the mic and start speaking</h5>
                                        <p className="text-muted small mb-0">I'll show transcription first and then write your note</p>
                                    </>
                                )}
                            </div>
                        ) : isRecording && !isPaused ? (
                            // Recording state
                            <div className="recording-state">
                                <div className="mic-icon-container mb-3 position-relative">
                                    <AudioVisualizer 
                                        audioLevel={audioLevel}
                                        isRecording={isRecording}
                                        microphoneStatus={microphoneStatus}
                                        size="large"
                                    />
                                </div>
                                <h5 className="text-success">Listening carefully. Continue speaking clearly and loudly into the mic</h5>

                                {/* Warning for web users only - native app can record in background */}
                                {!checkNativePlatform() && (
                                    <div className="alert alert-warning py-2 px-3 mb-2">
                                        <small>
                                            <i className="fas fa-exclamation-triangle me-1"></i>
                                            <strong>Web browser limitation:</strong> Recording will stop if you background the app or lock the screen. For uninterrupted recording, please use the mobile app.
                                        </small>
                                    </div>
                                )}

                                <p className="text-muted small mb-2">
                                    You can give me instructions on how to write each section in real-time.<br/>
                                    Just say "Sol, write HPI like this"
                                </p>
                                <div className="d-flex justify-content-center align-items-center gap-3">
                                    <small className="text-muted">
                                        <i className="fas fa-clock me-1"></i>
                                        {formatDuration(recordingDuration)}
                                    </small>
                                    <small className="text-muted">
                                        <i className="fas fa-layer-group me-1"></i>
                                        {audioSegments.length} segments
                                    </small>
                                    {enableRealtime && (
                                        <small className="text-success">
                                            <i className="fas fa-bolt me-1"></i>
                                            Real-time
                                        </small>
                                    )}
                                </div>
                            </div>
                        ) : isPaused ? (
                            // Paused state
                            <div className="paused-state">
                                <div className="mic-icon-container mb-3">
                                    <i className="fas fa-pause-circle fa-3x text-warning"></i>
                                </div>
                                <h5 className="text-warning">Recording Paused</h5>
                                <p className="text-muted small mb-2">Click resume to continue recording</p>
                                <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {formatDuration(recordingDuration)}
                                </small>
                            </div>
                        ) : (
                            // Stopped state
                            <div className="stopped-state">
                                <div className="mic-icon-container mb-3">
                                    <i className="fas fa-stop-circle fa-3x text-secondary"></i>
                                </div>
                                <h5 className="text-secondary">Recording Stopped</h5>
                                <p className="text-muted small mb-2">Session recorded successfully</p>
                                <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {formatDuration(recordingDuration)} | 
                                    <i className="fas fa-layer-group ms-2 me-1"></i>
                                    {audioSegments.length} segments
                                </small>
                            </div>
                        )}
                    </div>

                    {/* Real-time Transcription Box */}
                    {enableRealtime && isRecording && transcription && (
                        <div className="mx-3 mb-3">
                            <div className="card border-success">
                                <div className="card-header bg-success bg-opacity-10 py-2">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-circle text-danger me-2" style={{ fontSize: '0.6rem', animation: 'pulse 1.5s infinite' }}></i>
                                        <strong className="text-success">Live Transcript</strong>
                                        <span className="badge bg-success ms-2">Beta</span>
                                    </div>
                                </div>
                                <div className="card-body p-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    <p className="mb-0 text-dark">{transcription}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Recording Controls */}
                    <div className="text-center mb-3">
                        {hasStarted && (
                            <div className="btn-group me-2" role="group">
                                {!isPaused ? (
                                    <button className="btn btn-warning" onClick={handlePauseRecording} disabled={!isRecording}
                                    ><i className="fas fa-pause"></i></button>
                                ) : (
                                    <button className="btn btn-success" onClick={handleResumeRecording}
                                    ><i className="fa fa-play"></i></button>
                                )}
                                
                                <button className="btn btn-danger" onClick={handleStopRecording}
                                    disabled={!isRecording} >
                                    <i className="fas fa-stop"></i>
                                    
                                </button>
                            </div>
                        )}
                        
                        {(audioSegments.length > 0 || (hasStarted && !isRecording && !isPaused)) && (
                            <>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleGenerateNote}
                                    disabled={processingStatus === 'processing' || processingStatus === 'generating_note' || isRecording}
                                >
                                    {processingStatus === 'processing' || processingStatus === 'generating_note' ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-file-medical me-2"></i>
                                            Generate Note
                                        </>
                                    )}
                                </button>
                                {(processingStatus === 'processing' || processingStatus === 'generating_note') && (
                                    <div className="mt-2 text-muted small" style={{ minHeight: '2.5em' }}>
                                        {rotatingMessages[currentMessageIndex]}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    
                    {/* Show Recordings Link */}
                    {!showAllRecordings && (existingSessions.length > 0 || audioSegments.length > 0) && (
                        <div className="text-center mt-2">
                            
                        {hasStarted && (
                                <button className="btn btn-sm text-muted p-0" onClick={handleEndSession} >
                                 End Session  |   </button>
                        )} <button
                                type="button"
                                className="btn btn-sm text-muted p-0"
                                onClick={() => setShowAllRecordings(true)}
                            >
                                See recordings
                            </button>
                        </div>
                    )}
                    
                    {/* Audio Sessions and Segments */}
                    {/* Previous Recordings Component */}
                    {showAllRecordings && (
                        <AllRecordings
                            existingSessions={existingSessions}
                            audioSegments={audioSegments}
                            activeSessionDetails={activeSessionDetails}
                            loadingSessions={loadingSessions}
                            formatDuration={formatDuration}
                            onClose={() => setShowAllRecordings(false)}
                        />
                    )}
                    
                    
                    {/* Visit Note */}
                    {visitNote && (
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Generated Visit Note</h6>
                                <span className="badge bg-success">
                                    <i className="fas fa-check me-1"></i>
                                    Ready
                                </span>
                            </div>
                            <div className="card bg-light">
                                <div className="card-body" >
                                    <pre className="formatted-note mb-0" style={{
                                        overflowY: 'auto',
                                        maxHeight: '60vh',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {visitNote.content}
                                    </pre>
                                </div>
                            </div>
                            <small className="text-muted">
                                Generated from {visitNote.audioSegments} segments | 
                                Total duration: {formatDuration(visitNote.totalDuration)} |
                                Sources: {visitNote.audioSources?.join(', ')}
                            </small>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InPersonAudioRecorder;